<template>
  <div class="t-overflow-x-auto">
    <div
      class="t-align-middle t-inline-block t-min-w-full freight-table t-overflow-y-auto t-mt-2"
      role="table"
    >
      <div class="t-flex t-px-3 t-justify-between t-mb-2 t-font-semibold">
        <p class="t-w-28 t-flex t-flex-shrink-0">
          Status
        </p>
        <p class="t-w-32 t-px-3 content-center">Booking</p>
        <p class="t-flex t-flex-grow t-w-40 t-px-3">
          {{ freightType === "OUTGOING" ? "To" : "From" }}
        </p>
        <p class="t-w-12"></p>
        <p class="t-w-32 t-px-3">Due</p>
        <p class="t-w-28 t-py-2"></p>
      </div>

      <div
        v-if="bookingsLoadStatus"
        class="t-h-64 t-py-4 t-text-center t-text-2xl t-font-medium t-text-gray-900"
      >
        <i class="fas fa-x fa-spinner-third fa-spin"></i>
        Loading
      </div>

      <div
        v-if="!bookingsLoadStatus && bookings && bookings.length === 0"
        class="t-flex t-h-64 t-items-center t-justify-center t-h-16 t-text-center t-text-xl t-font-medium t-text-gray-900"
      >
        No {{ freightType === "OUTGOING" ? "outgoing" : "incoming" }} freight
        bookings
      </div>

      <!-- Bookings Table Main Content -->
      <div
        class="t-space-y-3"
        v-if="!bookingsLoadStatus && bookings && bookings.length > 0"
      >
        <div
          v-for="booking in bookings"
          :key="booking.id"
          class="t-flex t-px-3 t-justify-between t-bg-whitesmoke t-rounded-md"
        >
          <!-- Booking Status and Status Icons Column -->
          <div
            class="t-flex t-items-center t-w-28 t-whitespace-no-wrap t-space-y-2 t-justify-center t-font-medium"
          >
            <div
              class="t-inline-block t-w-full t-uppercase t-text-white t-rounded t-text-center t-px-2 t-py-1 t-bg-black"
              :class="[
                {
                  't-bg-orange':
                    formatBookingStatus(booking.status).status === 'BOOKED'
                },
                {
                  't-bg-dodger-blue':
                    formatBookingStatus(booking.status).status === 'IN_TRANSIT'
                },
                {
                  't-bg-forest-green':
                    formatBookingStatus(booking.status).status === 'DELIVERED'
                }
              ]"
            >
              {{ formatBookingStatus(booking.status).title }}
            </div>
          </div>
          <!-- End of Booking Status Column -->

          <!-- Booking Number Column -->
          <div class="t-flex t-items-center t-w-32 t-px-3 t-py-4 t-space-x-1">
            <div
              class="t-flex t-justify-center t-cursor-pointer"
              v-if="isBookingOnWatchlist(booking)"
              @click="onRemoveBookingFromWatchlist(booking)"
            >
              <star-icon class="t-fill-current t-text-yellow-500" />
            </div>
            <div
              class="t-flex t-justify-center t-cursor-pointer"
              v-else-if="!isBookingOnWatchlist(booking)"
              @click="onAddBookingToWatchlist(booking)"
            >
              <star-icon />
            </div>
            <a
              :href="'/send-freight/bookings/' + booking.id"
              style="color: #283acf;"
            >
              {{ booking.number }}
            </a>
          </div>
          <!-- End of Booking Number Column -->

          <!-- Location Column -->
          <div class="t-w-40 t-flex t-flex-grow t-p-3 t-items-center">
            <a-popover placement="top">
              <template>
                <span>
                  {{ locationInfo(booking).name }}
                </span>
              </template>
              <template slot="content">
                <div class="t-flex t-flex-col">
                  <span>
                    <i class="fa fa-map-marker" aria-hidden="true" />
                    <span>{{ locationInfo(booking).address }}</span>
                  </span>
                  <span v-if="locationInfo(booking).email">
                    <i class="fa fa-envelope" aria-hidden="true" />
                    <span>{{ locationInfo(booking).email }}</span>
                  </span>
                  <span v-if="locationInfo(booking).phone">
                    <i class="fa fa-phone" aria-hidden="true" />
                    <span>{{ locationInfo(booking).phone }}</span>
                  </span>
                </div>
              </template>
            </a-popover>
          </div>
          <!-- End of Location Column -->

          <!-- Status Icons -->
          <div
            class="t-flex t-items-center t-flex-wrap t-w-12 t-py-3 t-space-x-1"
          >
            <!-- Cube Icon  -->
            <a-popover v-if="booking.carrierName === 'Aramex'">
              <template slot="content">
                <span>This is a courier booking</span>
              </template>
              <i class="fa fa-cube" aria-hidden="true" />
            </a-popover>

            <!-- TailLift Icon -->
            <a-popover
              v-if="locationInfo(booking).requiresTailLift"
              placement="top"
            >
              <template slot="content">
                <span
                  >{{ locationInfo(booking).type }} requires a tail lift</span
                >
              </template>
              <tail-lift-icon class="t-flex t-w-4 t-h-4 t-mt-1" />
            </a-popover>

            <!-- Dangerous Goods Icon -->
            <a-popover v-if="hasDangerousGoods(booking)">
              <template slot="content">
                <span>Contains Dangerous Goods</span>
              </template>
              <icon-font-cn type="iconskeleton" class="t-mt-1" />
            </a-popover>

            <a-popover v-if="booking.pickupStop">
              <template slot="content">
                {{
                  formatTruckWaitingInfo(
                    booking.pickupStop.duration,
                    booking.pickupStop.waiting_time,
                    "pickup"
                  )
                }}
              </template>
              <load-truck-icon
                :style="{
                  fill: getWaitingIconColor(
                    Math.round(booking.pickupStop.waiting_time)
                  )
                }"
                class="t-w-4 t-h-4 t-mt-1"
              />
            </a-popover>

            <a-popover v-if="booking.deliveryStop">
              <template slot="content">
                <div>
                  {{
                    formatTruckWaitingInfo(
                      booking.deliveryStop.duration,
                      booking.deliveryStop.waiting_time,
                      "deliver"
                    )
                  }}
                </div>
              </template>
              <unload-truck-icon
                :style="{
                  fill: getWaitingIconColor(
                    Math.round(booking.deliveryStop.waiting_time)
                  )
                }"
                class="t-w-4 t-h-4 t-mt-1"
              />
            </a-popover>

            <!-- Source Icon -->
            <!--<a-popover v-if="false" v-if="booking.latestFreightEvent.type !== 'TYPE_UNKNOWN' && booking.latestFreightEvent.source">-->
            <a-popover v-if="false">
              <template slot="content">
                <span>
                  {{ booking.latestFreightEvent.type + " " }}
                  {{ getSourceText(booking.latestFreightEvent.source) }}
                </span>
              </template>
              <div class="t-inline-block t-w-4 t-h-4 t-mt-1">
                <i
                  :class="getSourceIcon(booking.latestFreightEvent.source)"
                  aria-hidden="true"
                />
              </div>
            </a-popover>

            <!-- Tracker Icon -->
            <a-popover v-if="booking.tracker">
              <template slot="content">
                <span>{{
                  formatTrackerIconText(
                    booking.trackerStatusBand,
                    booking.status,
                    booking
                  )
                }}</span>
              </template>
              <i
                class="fas fa-link t-mt-1"
                :style="{
                  color: formatTrackerIconColor(
                    booking.trackerStatusBand,
                    booking.status
                  )
                }"
              />
            </a-popover>
          </div>

          <!-- Due Column -->
          <div class="t-w-32 t-p-3 t-flex t-flex-col t-justify-center">
            <p :class="{ 't-line-through': booking.timing > 0 }">
              {{ formatCalendarTime(booking.deliverBy) }}
            </p>
            <p
              v-if="
                booking.timing > 0 &&
                  booking.deliveryTime &&
                  booking.status === 'Delivered'
              "
            >
              {{ formatCalendarTime(booking.deliveryTime) }}
            </p>
            <p v-else-if="booking.timing > 0 && booking.eta > moment()">
              {{ formatCalendarTime(booking.eta) }}
            </p>

            <div>
              <!-- Booking Delayed Red Clock -->
              <a-popover v-if="booking.timing > 0" placement="top">
                <template slot="content">
                  <span v-if="bookingHasUnknownDelay(booking)">
                    Behind the schedule, please
                    <a href="/contact" target="_blank"> contact Freighthub</a>
                    for more details
                  </span>
                  <span v-else>{{ getRedClockPopoverText(booking) }}</span>
                </template>

                <div style="color: red">
                  <i class="fa fa-clock" aria-hidden="true" />
                  <span>{{ "+" + booking.timing }}</span>
                </div>
              </a-popover>

              <!-- Booking Advanced Green Clock -->
              <a-popover
                v-if="booking.timing < 0 && booking.status === 'Delivered'"
                placement="top"
              >
                <template slot="content">
                  <span>{{ getGreenClockPopoverText(booking) }}</span>
                </template>

                <div style="color: green">
                  <i class="fa fa-clock" aria-hidden="true"></i>
                  {{ booking.timing }}
                </div>
              </a-popover>
            </div>
          </div>
          <!-- End of Due Column -->

          <!-- Options -->
          <div
            class="t-flex t-flex-col t-justify-center t-w-28 t-px-1 t-py-2 t-space-y-2"
          >
            <button
              v-if="!booking.tracker"
              @click="trackBooking(booking)"
              class="t-block t-rounded-md t-w-full t-px-3 t-py-1 t-bg-dodger-blue t-text-sm t-font-medium t-text-white hover:t-bg-blue-500"
            >
              View Map
            </button>

            <button
              v-if="
                freightType !== 'INCOMING' &&
                  !booking.tracker &&
                  booking.status_id >= 2 &&
                  booking.status_id <= 3
              "
              @click="onLinkTrackerClick(booking)"
              class="t-block t-rounded-md t-w-full t-px-2 t-py-1 t-text-black t-text-sm t-border t-border-dodger-blue hover:t-bg-blue-500 hover:t-text-white"
            >
              Link Tracker
            </button>

            <button
              v-if="booking.tracker"
              @click="trackBooking(booking)"
              class="t-block t-rounded-md t-w-full t-px-3 t-py-1 t-bg-dodger-blue t-text-sm t-text-white hover:t-bg-blue-500"
            >
              <i class="fas fa-link" />
              Track
            </button>
          </div>
          <!-- End of Options Column -->
        </div>
      </div>
      <!-- End of Bookings Table Main Content -->

      <add-tracker-modal
        v-if="selectedBooking"
        :booking="selectedBooking"
        :is-showing-tracker-modal="isShowingTrackerModal"
        @modal-cancelled="onTrackerModalCancel"
        @modal-submitted="onTrackerModalSubmit"
      />
    </div>
  </div>
</template>

<script>
import moment from "moment";
import AddTrackerModal from "../../../modals/AddTrackerModal";
import StarIcon from "../../../icons/StarIcon";
import FilterIcon from "../../../icons/FilterIcon";
import LoadTruckIcon from "../../../icons/LoadTruckIcon";
import UnloadTruckIcon from "../../../icons/UnloadTruckIcon";
import IconFontCn from "../../../icons/IconFontCn";
import TailLiftIcon from "../../../icons/TailLiftIcon";

export default {
  name: "DashboardFreightTable",
  description: "",
  components: {
    AddTrackerModal,
    StarIcon,
    FilterIcon,
    LoadTruckIcon,
    UnloadTruckIcon,
    IconFontCn,
    TailLiftIcon
  },
  props: {
    freightBookings: {
      type: Array
    },
    watchlistFreightBookings: {
      type: Array
    },
    freightType: {}
  },
  emits: ["bookingSelected"],
  data() {
    return {
      moment,
      bookings: [],
      selectedBooking: null,
      showFilterOptions: false,
      isShowingTrackerModal: false
    };
  },
  computed: {
    bookingsLoadStatus() {
      return (
        this.$store.getters.getBookingsLoadStatus === 1 ||
        this.$store.getters.getDefaultPickupLocationLoadStatus === 1
      );
    },
    locationInfo() {
      return booking => {
        let freightIsIncoming = this.freightType === "INCOMING";
        let locationType = freightIsIncoming
          ? "pickupLocation"
          : "deliveryLocation";
        let type = freightIsIncoming ? "Pickup" : "Delivery";
        let requiresTailLift = freightIsIncoming
          ? booking.pickupRequiresTailLift
          : booking.deliveryRequiresTailLift;

        return {
          ...booking[locationType],
          requiresTailLift: requiresTailLift,
          type: type
        };
      };
    }
  },
  mounted() {
    this.$initGoogleMap('map');
  },
  created() {
    if (!this.freightBookings) {
      return;
    }
    this.bookings = this.orderBookings(this.freightBookings);
  },
  methods: {
    orderBookings(bookings) {
      // Order the bookings so that watched items are at the top
      let watchedBookings = [];
      let bookedList = [];
      let inTransitList = [];
      let deliveredList = [];
      //let unwatchedBookings = [];
      bookings.forEach(booking => {
        if (this.isBookingOnWatchlist(booking)) {
          watchedBookings.push(booking);
        } else {
          let status = booking.status.split(","); //special for status booke with ,
          if (status[0] == "Booked") {
            bookedList.push(booking);
          }
          if (status[0] == "In transit") {
            inTransitList.push(booking);
          }
          if (status[0] == "Delivered") {
            deliveredList.push(booking);
          }
          //unwatchedBookings.push(booking);
        }
      });
      //return watchedBookings.concat(unwatchedBookings);
      return watchedBookings.concat(
        bookedList.concat(inTransitList.concat(deliveredList))
      );
    },

    formatLocationData(booking, freightType) {
      if (freightType === "INCOMING") {
        return {
          ...booking["pickupLocation"],
          requiresTailLift: booking.pickupRequiresTailLift,
          transportCategory: "Pickup"
        };
      } else {
        return {
          ...booking["deliveryLocation"],
          requiresTailLift: booking.deliveryRequiresTailLift,
          transportCategory: "Delivery"
        };
      }
    },

    formatBookingStatus(bookingStatus) {
      if (!bookingStatus) {
        return;
      }
      bookingStatus = bookingStatus.toLowerCase();
      let pendingString = "pending";
      let bookedString = "booked";
      let inTransitString = "in transit";
      let deliveredString = "delivered";

      if (bookingStatus.includes(pendingString)) {
        return {
          title: "Pending",
          status: "PENDING"
        };
      } else if (bookingStatus.includes(bookedString)) {
        return {
          title: "Booked",
          status: "BOOKED"
        };
      } else if (bookingStatus.includes(inTransitString)) {
        return {
          title: "In Transit",
          subtitle: bookingStatus.split(",")[1],
          status: "IN_TRANSIT"
        };
      } else if (bookingStatus.includes(deliveredString)) {
        return {
          title: "Delivered",
          status: "DELIVERED"
        };
      } else {
        return {
          title: bookingStatus,
          status: bookingStatus
        };
      }
    },

    formatCalendarTime(time) {
      return time
        ? time.calendar(null, {
            sameDay: "[Today]",
            nextDay: "[Tomorrow]",
            nextWeek: "ddd DD MMM",
            lastDay: "[Yesterday]",
            lastWeek: function callback(now) {
              return now.isoWeek() === time.isoWeek() ? "ddd" : "[Last] ddd";
            },
            sameElse: "Do MMM"
          })
        : null;
    },

    isBookingOnWatchlist(booking) {
      return booking.watchlist && Number.parseInt(booking.watchlist.id) > 0;
    },

    onAddBookingToWatchlist(booking) {
      const params = { reason: "source: Sender Dashboard" };
      this.$store.dispatch("addBookingWatchlist", { booking, params });
    },

    onRemoveBookingFromWatchlist(booking) {
      this.$store.dispatch("deleteBookingWatchlist", booking);
    },

    onLinkTrackerClick(booking) {
      this.selectedBooking = booking;
      this.isShowingTrackerModal = true;
    },

    onTrackerModalCancel() {
      this.selectedBooking = null;
      this.isShowingTrackerModal = false;
    },

    onTrackerModalSubmit() {
      this.selectedBooking = null;
      this.isShowingTrackerModal = false;
    },

    /*
     * ----- Booking Status Icon Related Functions --
     */
    formatTruckWaitingInfo(stopDuration, waitingDuration, action) {
      let stopTime = Math.round(stopDuration);
      let waitingTime = Math.round(waitingDuration);
      let expectedWaitingTime = 10;

      const diff = waitingTime - expectedWaitingTime;
      let comparison = "as expected";
      if (diff > 0) {
        comparison = diff + " minutes more than expected";
      }
      if (diff < 0) {
        comparison = Math.abs(diff) + " minutes less than expected";
      }
      return `Truck stopped for ${stopTime} minutes to ${action} freight, ${comparison}.`;
    },

    getSourceIcon(source) {
      const SOURCE_ICONS = {
        SOURCE_GPS_SERVICE: "fa fa-location-arrow",
        SOURCE_API_INTEGRATION: "fa fa-server",
        SOURCE_USER_REQUEST: "fa fa-street-view"
      };
      return SOURCE_ICONS[source];
    },

    getSourceText(source) {
      const SOURCE_TEXT = {
        SOURCE_GPS_SERVICE: "detected from GPS",
        SOURCE_API_INTEGRATION: "confirmed by 3rd party API",
        SOURCE_USER_REQUEST: "confirmed by Freighthub"
      };
      return SOURCE_TEXT[source];
    },

    hasDangerousGoods(booking) {
      if (booking.lines.length < 1) {
        return false;
      }

      let dgLines = booking.lines.filter(line =>
        line.goodsType.includes("Containing Dangerous Goods")
      );
      return dgLines.length > 0;
    },

    formatTrackerIconText(band, status, booking) {
      if (status.includes("Booked")) {
        return "This booking has a blue-tooth FreightMate attached to it to provide real-time tracking";
      }
      if (status === "Delivered") {
        return "This booking has been delivered";
      }
      if (band === 0) {
        return "We currently have no information about this tracker";
      }
      let lastPingAgo = moment
        .utc(booking.tracker.last_location_time)
        .fromNow();
      if (band === 1 || band === 2) {
        return "Moving (last detected " + lastPingAgo + ")";
      } else if (band === 5) {
        return "Last detected " + lastPingAgo;
      }
      let lastTruckStopAgo = moment
        .utc(booking.latestFreightEvent.time)
        .fromNow(true);
      return (
        "Stopped for " +
        lastTruckStopAgo +
        " (last detected " +
        lastPingAgo +
        ")"
      );
    },

    formatTrackerIconColor(band, status) {
      if (status === "Delivered" || band === 0 || status.includes("Booked")) {
        return "";
      }

      if (band === 1) {
        return "green";
      } else if (band === 2) {
        return "orange";
      } else if (band === 3) {
        return "orange";
      } else if (band === 4) {
        return "red";
      } else if (band === 5) {
        return "blue";
      }
      return "";
    },

    /**
     *
     * @param waitingTime
     * @returns {string}
     */
    getWaitingIconColor(waitingTime) {
      let expectedWaitingTime = 10;
      const diff = waitingTime - expectedWaitingTime;
      if (diff > 0) {
        return diff > 10 ? "red" : "orange ";
      }
      return "green";
    },

    bookingHasUnknownDelay(booking) {
      return booking.eta < moment() && booking.deliveryTime === null;
    },

    getRedClockPopoverText(booking) {
      if (booking.timing > 0) {
        return (
          Math.abs(booking.timing) +
          (Math.abs(booking.timing) > 1 ? " days" : " day") +
          " behind the schedule"
        );
      }
      return "Behind the schedule";
    },

    getGreenClockPopoverText(booking) {
      if (booking.timing < 0) {
        return (
          "Delivered " +
          Math.abs(booking.timing) +
          (Math.abs(booking.timing) > 1 ? " days" : " day") +
          " ahead of schedule"
        );
      }
      return "Delivered ahead of schedule";
    },

    trackBooking(booking) {
      if (this.freightType === "OUTGOING") {
        this.$emit("outgoingBookingSelected", booking);
      } else {
        this.$emit("incomingBookingSelected", booking);
      }
    }
  },
  watch: {
    freightBookings(newFreightBookings) {
      if (!newFreightBookings) {
        return;
      }
      this.bookings = this.orderBookings(newFreightBookings);
    }
  }
};
</script>

<style scoped>
.freight-table {
  min-height: 16rem;
  max-height: 24rem;
}

i.anticon {
  height: 1rem;
}
</style>
